<template>
  <div class="files-page">
    <!-- first load: skeleton tiles in the same grid the real list uses -->
    <div v-if="fieldsLoading" class="files-browser">
      <div class="files-toolbar">
        <v-skeleton-loader type="text" class="files-skeleton-search"></v-skeleton-loader>
      </div>
      <div class="files-grid files-grid--skeleton">
        <v-skeleton-loader v-for="n in 8" :key="n" type="image" class="files-skeleton-tile"></v-skeleton-loader>
      </div>
    </div>

    <template v-else>
      <!-- ───────────── Browser ───────────── -->
      <div v-if="!activeHtmlLink" class="files-browser">
        <div class="files-toolbar">
          <v-text-field
            class="files-search"
            v-model="fileSearchTerm"
            :placeholder="$t('message.common.search')"
            prepend-inner-icon="mdi-magnify"
            clearable
            hide-details
            single-line
            outlined
            dense
            @input="onSearchInput"
            @click:clear="clearSearch"
          ></v-text-field>
          <v-btn-toggle :value="viewMode" mandatory dense class="files-view-toggle" @change="setViewMode">
            <v-btn value="grid" small icon :title="$t('message.common.gridView')" :aria-label="$t('message.common.gridView')">
              <v-icon small>mdi-view-grid</v-icon>
            </v-btn>
            <v-btn value="list" small icon :title="$t('message.common.listView')" :aria-label="$t('message.common.listView')">
              <v-icon small>mdi-format-list-bulleted</v-icon>
            </v-btn>
          </v-btn-toggle>
        </div>

        <!-- location: back-to-parent + scrollable breadcrumb, or the search header -->
        <div class="files-crumbs" v-if="!isSearching">
          <v-btn icon small class="files-up" :disabled="breadCrumbItems.length <= 2" :title="$t('message.common.back')" :aria-label="$t('message.common.back')" @click="goUp">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <div class="files-crumb-scroll" ref="crumbScroll">
            <template v-for="(item, index) in breadCrumbItems">
              <v-icon v-if="index" :key="`sep_${index}`" small class="files-crumb-sep">mdi-chevron-right</v-icon>
              <button
                :key="`crumb_${item.id}`"
                type="button"
                class="files-crumb"
                :class="{ 'files-crumb--current': index === breadCrumbItems.length - 1 }"
                :disabled="index === breadCrumbItems.length - 1"
                @click="navigateToItem(item)"
              >
                <v-icon v-if="item.isHome" small class="files-crumb-home">mdi-home-outline</v-icon>
                <span v-else>{{ crumbLabel(item) }}</span>
              </button>
            </template>
          </div>
        </div>
        <div class="files-crumbs files-crumbs--search" v-else>
          <v-icon small class="mr-1">mdi-magnify</v-icon>
          <span class="files-crumb files-crumb--current">{{ $t('message.common.searchResults') }}</span>
          <span class="files-count" v-if="!searchLoading">{{ listOfSearchResult.length }}</span>
        </div>

        <v-progress-linear indeterminate height="2" :active="loading || searchLoading" class="files-progress"></v-progress-linear>

        <!-- the previous listing stays visible (dimmed) while the next one loads: no flash to empty -->
        <div class="files-content" :class="{ 'files-content--stale': loading || searchLoading }">
          <template v-if="visibleFolders.length || visibleFiles.length">
            <!-- GRID -->
            <template v-if="viewMode === 'grid'">
              <section v-if="visibleFolders.length" class="files-section">
                <h3 class="files-section-title">{{ $t('message.common.folders') }} <span class="files-count">{{ visibleFolders.length }}</span></h3>
                <div class="files-grid">
                  <button v-for="doc in visibleFolders" :key="`folder_${doc.id}`" type="button" class="files-tile files-tile--folder" @click="openDocument(doc)">
                    <span class="files-tile-icon" :style="iconBadgeStyle(doc)">
                      <v-icon :color="iconOf(doc).color" size="30">{{ iconOf(doc).icon }}</v-icon>
                    </span>
                    <span class="files-tile-name">{{ nameOf(doc) }}</span>
                    <span class="files-tile-meta">{{ isSearching && doc.parent_folder_name ? doc.parent_folder_name : $t('message.common.folder') }}</span>
                  </button>
                </div>
              </section>
              <section v-if="visibleFiles.length" class="files-section">
                <h3 class="files-section-title">{{ $t('message.common.files') }} <span class="files-count">{{ visibleFiles.length }}</span></h3>
                <div class="files-grid">
                  <button v-for="doc in visibleFiles" :key="`file_${doc.id}`" type="button" class="files-tile" @click="openDocument(doc)">
                    <span class="files-tile-icon" :style="iconBadgeStyle(doc)">
                      <v-icon :color="iconOf(doc).color" size="30">{{ iconOf(doc).icon }}</v-icon>
                    </span>
                    <span class="files-tile-name">{{ nameOf(doc) }}</span>
                    <span class="files-tile-meta">{{ metaOf(doc) }}</span>
                  </button>
                </div>
              </section>
            </template>

            <!-- LIST -->
            <v-list v-else class="files-list pa-0" dense>
              <v-list-item v-for="doc in visibleFolders.concat(visibleFiles)" :key="`row_${doc.id}`" class="files-row px-2" @click="openDocument(doc)">
                <v-list-item-avatar tile size="38" class="files-row-icon my-1 mr-3" :style="iconBadgeStyle(doc)">
                  <v-icon :color="iconOf(doc).color" size="24">{{ iconOf(doc).icon }}</v-icon>
                </v-list-item-avatar>
                <v-list-item-content class="py-2">
                  <v-list-item-title class="files-row-name">{{ nameOf(doc) }}</v-list-item-title>
                  <v-list-item-subtitle class="files-row-meta">{{ metaOf(doc) }}</v-list-item-subtitle>
                </v-list-item-content>
                <v-list-item-action class="my-0">
                  <v-icon small class="files-row-chevron">mdi-chevron-right</v-icon>
                </v-list-item-action>
              </v-list-item>
            </v-list>
          </template>

          <!-- EMPTY -->
          <div v-else-if="!loading && !searchLoading" class="files-empty">
            <v-icon size="56" class="files-empty-icon">{{ isSearching ? 'mdi-file-search-outline' : 'mdi-folder-open-outline' }}</v-icon>
            <p class="files-empty-title">{{ isSearching ? $t('message.common.noResults') : $t('message.common.emptyFolder') }}</p>
            <p class="files-empty-hint" v-if="!isSearching">{{ $t('message.common.emptyFolderHint') }}</p>
          </div>
        </div>
      </div>

      <!-- ───────────── Viewer ───────────── -->
      <v-card v-else flat class="files-viewer">
        <div class="files-viewer-head">
          <v-btn icon small :title="$t('message.common.back')" :aria-label="$t('message.common.back')" @click="closeViewer">
            <v-icon>mdi-arrow-left</v-icon>
          </v-btn>
          <span class="files-viewer-icon" v-if="activeDocument" :style="iconBadgeStyle(activeDocument)">
            <v-icon :color="iconOf(activeDocument).color" size="20">{{ iconOf(activeDocument).icon }}</v-icon>
          </span>
          <span class="files-viewer-title text-truncate" :title="activeHtmlTitle">{{ activeHtmlTitle }}</span>
          <span class="files-viewer-nav" v-if="activeDocuments.length > 1">
            <v-btn icon small :disabled="!previous_document" :title="$t('message.common.previousDocument')" :aria-label="$t('message.common.previousDocument')" @click="loadHtmlFile(previous_document)">
              <v-icon>mdi-chevron-left</v-icon>
            </v-btn>
            <span class="files-viewer-counter">{{ viewerIndex + 1 }} / {{ activeDocuments.length }}</span>
            <v-btn icon small :disabled="!next_document" :title="$t('message.common.nextDocument')" :aria-label="$t('message.common.nextDocument')" @click="loadHtmlFile(next_document)">
              <v-icon>mdi-chevron-right</v-icon>
            </v-btn>
          </span>
        </div>
        <div ref="pdfViewport" class="main_pdf_view" :class="{ 'main_pdf_view--fit': fitToScreen }">
          <v-row class="mx-0" :style="rowSizingStyle">
            <v-progress-linear indeterminate color="primary" v-if="iframeSrcLoading && !showPdfPreviewDialog"></v-progress-linear>
            <v-responsive v-if="showPdfPreviewDialog" :aspect-ratio="16/9" >
              <!-- PDFs re-render their canvases at the given width, so zoom is a real
                   width change here rather than the iframe's CSS transform. The key
                   remounts the viewer when the document changes: it loads its task
                   once on mount and would otherwise keep showing the previous PDF. -->
              <pdf-viewer :key="activeHtmlLink" :fileUrl="activeHtmlLink" :renderWidth="pdfRenderWidth" @error="onDocumentError"></pdf-viewer>
            </v-responsive>
            <v-responsive  v-else :aspect-ratio="16/9" :style="`visibility: ${iframeSrcLoading ? 'hidden' : ''}`">
              <!-- Zoom/fit keep the iframe at a wide layout viewport and scale it
                   instead: resizing its box alone can't rescale the inner document. -->
              <iframe
                @load="iframeSrcLoading = false"
                class="files-html"
                :width="isScaled ? `${fitBaseWidth}px` : '100%'"
                :style="isScaled ? fitTransformStyle : null"
                :src="activeHtmlLink"
              ></iframe>
            </v-responsive>
          </v-row>
        </div>
        <div class="button__box">
          <button :title="$t('message.common.zoomIn')" :disabled="!canZoomIn" @click="zoomIn">+</button>
          <button
            class="button__box-fit"
            :title="fitToScreen ? $t('message.common.actualSize') : $t('message.common.fitToScreen')"
            @click="toggleFitToScreen"
          >
            <!-- MDI 5.x (see public/index.html) has no mdi-fit-to-screen* icons -->
            <v-icon color="white">{{ fitToScreen ? 'mdi-fullscreen-exit' : 'mdi-fullscreen' }}</v-icon>
          </button>
          <button :title="$t('message.common.zoomOut')" :disabled="!canZoomOut" @click="zoomOut">-</button>
        </div>
      </v-card>
    </template>
  </div>
</template>
<script>
import { hostAppApi } from '@/plugins/axios_settings'
import { mapGetters } from 'vuex'

const VIEW_MODE_KEY = 'WelditPwa_filesView'
const ROOT001 = 'root001'
const PARENT = 'parent'
const CHILDREN = 'childrens'

/* icon + accent per file type; the badge behind it is the same colour at low alpha.
   Folders use the dashboard module tiles' blue chip (dashboard-layout.css .dash-fcell-ic)
   so the Files screen reads as a continuation of the "Dokumenter" tile. */
const FOLDER_ICON = { icon: 'mdi-folder', color: '#0377e8' }
const FOLDER_BADGE = { light: '#e9f1fd', dark: '#1e2d44' }
const FILE_ICONS = {
  pdf: { icon: 'mdi-file-pdf', color: '#e53935' },
  doc: { icon: 'mdi-file-word', color: '#1e88e5' },
  docx: { icon: 'mdi-file-word', color: '#1e88e5' },
  odt: { icon: 'mdi-file-word', color: '#1e88e5' },
  rtf: { icon: 'mdi-file-word', color: '#1e88e5' },
  xls: { icon: 'mdi-file-excel', color: '#43a047' },
  xlsx: { icon: 'mdi-file-excel', color: '#43a047' },
  csv: { icon: 'mdi-file-excel', color: '#43a047' },
  ppt: { icon: 'mdi-file-powerpoint', color: '#fb8c00' },
  pptx: { icon: 'mdi-file-powerpoint', color: '#fb8c00' },
  jpg: { icon: 'mdi-file-image', color: '#8e24aa' },
  jpeg: { icon: 'mdi-file-image', color: '#8e24aa' },
  png: { icon: 'mdi-file-image', color: '#8e24aa' },
  gif: { icon: 'mdi-file-image', color: '#8e24aa' },
  svg: { icon: 'mdi-file-image', color: '#8e24aa' },
  webp: { icon: 'mdi-file-image', color: '#8e24aa' },
  html: { icon: 'mdi-language-html5', color: '#f16428' },
  htm: { icon: 'mdi-language-html5', color: '#f16428' }
}
const DEFAULT_FILE_ICON = { icon: 'mdi-file-document-outline', color: '#607d8b' }

export default {
  data () {
    return {
      fieldsLoading: false,
      /* For files */
      listOfDocuments: [],
      loading: false,
      breadCrumbItems: [{ id: 0, text: this.$t('message.layout.home'), isHome: true }],
      activeFolder: null,
      activeHtmlTitle: '',
      activeHtmlLink: '',
      iframeSrcLoading: false,
      fileSearchTerm: '',
      searchLoading: false,
      userId: 0,
      listOfSearchResult: [],
      debouncedSearchPublishedFiles: null,
      showPdfPreviewDialog: false,
      /* the files (display order) the open document was picked from, for prev/next */
      activeDocuments: [],
      activeDocument: null,
      viewerIndex: -1,
      viewMode: 'grid',
      /* Folder listings already fetched this visit, keyed by folder id. Going back up
         the tree renders instantly from here while the fresh copy is fetched. */
      folderCache: {},
      /* Monotonic request counters: a response is applied only when it belongs to
         the latest request, so fast taps through folders can't land out of order. */
      folderRequestSeq: 0,
      searchRequestSeq: 0,
      widthPdf: 700,
      /* Zoom/fit: render the iframe at `fitBaseWidth` (a desktop-ish layout viewport,
         so the document lays out as intended) then scale it. Fit picks the scale that
         makes it exactly fill the container; +/- step that same scale. */
      fitToScreen: false,
      fitBaseWidth: 1024,
      fitScale: 1,
      viewportWidth: 0,
      zoomStep: 0.15,
      minZoom: 0.25,
      maxZoom: 3
    }
  },
  components: {
    'pdf-viewer': () => import('@/components/PdfViewer/PdfViewer.vue')
  },
  computed: {
    ...mapGetters(['getHostRefApi']),
    isSearching () {
      return (this.fileSearchTerm || '').trim().length > 0
    },
    currentList () {
      return this.isSearching ? this.listOfSearchResult : this.listOfDocuments
    },
    /* folders first, then files, each in the order the server sorted them */
    visibleFolders () {
      return this.currentList.filter(doc => doc && doc.id && Number(doc.is_dir) === 1)
    },
    visibleFiles () {
      return this.currentList.filter(doc => doc && doc.id && Number(doc.is_dir) !== 1)
    },
    previous_document () {
      return this.viewerIndex > 0 ? this.activeDocuments[this.viewerIndex - 1] : false
    },
    next_document () {
      return this.viewerIndex >= 0 && this.viewerIndex < this.activeDocuments.length - 1 ? this.activeDocuments[this.viewerIndex + 1] : false
    },
    /* Any scale other than 1:1 means the iframe is in wide-layout + transform mode. */
    isScaled () {
      return this.fitToScreen || this.fitScale !== 1
    },
    canZoomIn () {
      return this.isScaled ? this.fitScale < this.maxZoom : true
    },
    canZoomOut () {
      return this.isScaled ? this.fitScale > this.minZoom : true
    },
    /* PDF pages are re-rendered at this width, so zoom/fit is genuine resolution
       rather than a transform. Fit means "exactly the viewport width". */
    pdfRenderWidth () {
      if (this.fitToScreen) return Math.round(this.viewportWidth || this.fitBaseWidth)
      return Math.round(this.fitBaseWidth * this.fitScale)
    },
    /* A transformed element doesn't grow its parent, so when zoomed past 1:1 the row
       has to claim the scaled width itself or there'd be nothing to scroll to.
       The PDF branch sizes itself, so leave the row alone there. */
    rowSizingStyle () {
      if (this.showPdfPreviewDialog) return {}
      if (!this.isScaled) return { minWidth: `${this.widthPdf}px` }
      return { minWidth: `${Math.round(this.fitBaseWidth * this.fitScale)}px` }
    },
    /* `transform: scale` doesn't change layout size, so the untouched height stays
       reserved below the iframe — the negative margin reclaims that dead space.
       Scaling up (>1) instead needs extra height, hence the sign flip. */
    fitTransformStyle () {
      const reclaim = (1 - this.fitScale) * 100
      return {
        transform: `scale(${this.fitScale})`,
        transformOrigin: 'top left',
        marginBottom: `${-reclaim}%`
      }
    }
  },
  mounted () {
    this.userId = this.$cookie.get(process.env.VUE_APP_HOST_USER) ? this.$cookie.get(process.env.VUE_APP_HOST_USER) : 0
    try {
      const saved = window.localStorage.getItem(VIEW_MODE_KEY)
      if (saved === 'grid' || saved === 'list') this.viewMode = saved
    } catch (e) { /* storage unavailable: keep the default */ }
    this.debouncedSearchPublishedFiles = this.$formatter.debounce(this.searchPublishedDocuments, 450)
    this.loadRootFolder(true)
    this.onViewportResize = this.$formatter.debounce(this.recalculateFitScale, 150)
    window.addEventListener('resize', this.onViewportResize)
    this.$nextTick(this.recalculateFitScale)
  },
  beforeDestroy () {
    if (this.onViewportResize) window.removeEventListener('resize', this.onViewportResize)
  },
  methods: {
    /* ── presentation helpers ───────────────────────────────────────────── */
    nameOf (doc) {
      return doc.text || doc.original_name || ''
    },
    crumbLabel (item) {
      return item.text || item.original_name || ''
    },
    iconOf (doc) {
      if (Number(doc.is_dir) === 1) return FOLDER_ICON
      const ext = (doc.ext || '').toLowerCase()
      return FILE_ICONS[ext] || DEFAULT_FILE_ICON
    },
    iconBadgeStyle (doc) {
      if (Number(doc.is_dir) === 1) {
        return { backgroundColor: this.$vuetify.theme.dark ? FOLDER_BADGE.dark : FOLDER_BADGE.light }
      }
      return { backgroundColor: this.tint(this.iconOf(doc).color, 0.12) }
    },
    /* '#rrggbb' at the given alpha; the badge behind an icon is its own colour, faded */
    tint (hex, alpha) {
      const m = /^#?([0-9a-f]{6})$/i.exec(hex || '')
      if (!m) return 'transparent'
      const n = parseInt(m[1], 16)
      return `rgba(${(n >> 16) & 255}, ${(n >> 8) & 255}, ${n & 255}, ${alpha})`
    },
    metaOf (doc) {
      if (Number(doc.is_dir) === 1) {
        return this.isSearching && doc.parent_folder_name ? doc.parent_folder_name : this.$t('message.common.folder')
      }
      const parts = []
      if (this.isSearching && doc.parent_folder_name) parts.push(doc.parent_folder_name)
      if (doc.ext) parts.push(doc.ext.toUpperCase())
      const size = this.formatSize(doc.file_size)
      if (size) parts.push(size)
      if (doc.revision_no) parts.push(`Rev. ${doc.revision_no}`)
      return parts.join(' · ')
    },
    formatSize (bytes) {
      const n = Number(bytes)
      if (!n || n <= 0) return ''
      if (n < 1024) return `${n} B`
      if (n < 1024 * 1024) return `${Math.round(n / 1024)} KB`
      return `${(n / (1024 * 1024)).toFixed(n < 10 * 1024 * 1024 ? 1 : 0)} MB`
    },
    setViewMode (mode) {
      if (mode !== 'grid' && mode !== 'list') return
      this.viewMode = mode
      try { window.localStorage.setItem(VIEW_MODE_KEY, mode) } catch (e) { /* per-device convenience only */ }
    },
    /* ── navigation ─────────────────────────────────────────────────────── */
    openDocument (doc) {
      if (Number(doc.is_dir) === 1) this.getChildren(doc)
      else this.loadHtmlFile(doc)
    },
    goUp () {
      if (this.breadCrumbItems.length > 2) this.navigateToItem(this.breadCrumbItems[this.breadCrumbItems.length - 2])
    },
    navigateToItem (item) {
      if (!item) return
      if (item.isHome) this.$router.push('/dashboard')
      else if (item.id === ROOT001) this.loadRootFolder(false)
      else this.getChildren(item)
    },
    /* Breadcrumb bookkeeping happens when navigation starts, not when the response
       lands, so it runs once per move however many renders (cache, then fresh) follow. */
    trimBreadCrumbsTo (folder) {
      const index = this.breadCrumbItems.findIndex(x => x.id === folder.id)
      if (index !== -1) this.breadCrumbItems.splice(index + 1, this.breadCrumbItems.length)
      else this.breadCrumbItems.push(folder)
      this.$nextTick(() => {
        const el = this.$refs.crumbScroll
        if (el) el.scrollLeft = el.scrollWidth
      })
    },
    loadRootFolder (isFirstCall) {
      if (isFirstCall) this.fieldsLoading = true
      // the root crumb (its name comes from the server) may already be known from a previous visit
      const rootCrumb = this.breadCrumbItems.find(x => x.id === ROOT001) || (this.folderCache[ROOT001] && this.folderCache[ROOT001].parent)
      this.breadCrumbItems.splice(1, this.breadCrumbItems.length)
      if (rootCrumb) this.breadCrumbItems.push(rootCrumb)
      this.requestFolder(ROOT001, { id: ROOT001, user_id: this.userId }, (data) => {
        if (data.parent && !this.breadCrumbItems.find(x => x.id === ROOT001)) this.breadCrumbItems.push(data.parent)
        this.activeFolder = data.parent || { id: ROOT001 }
        this.listOfDocuments = data.documents
      })
    },
    getChildren (selectedFolder) { // it will have id and its parent id
      const folder = this.$formatter.cloneVariable(selectedFolder)
      this.trimBreadCrumbsTo(folder)
      this.activeFolder = folder
      this.requestFolder(folder.id, { id: folder.id, parent_id: folder.parent_id, user_id: this.userId }, (data) => {
        this.listOfDocuments = data.documents
      })
    },
    /* Render from cache at once (when we have it), fetch, then re-render from the
       fresh copy — but only if this is still the latest navigation. */
    requestFolder (key, payload, apply) {
      const seq = ++this.folderRequestSeq
      const cached = this.folderCache[key]
      if (cached) apply(cached)
      this.loading = true
      hostAppApi.post(`${this.getHostRefApi}get_published_documents`, payload)
        .then((response) => {
          if (seq !== this.folderRequestSeq) return
          const body = response && response.data ? response.data : {}
          const data = key === ROOT001
            ? { parent: body[PARENT] || null, documents: this.normalizeDocuments(body[CHILDREN]) }
            : { parent: null, documents: this.normalizeDocuments(body) }
          this.folderCache = { ...this.folderCache, [key]: data }
          apply(data)
        })
        .catch(() => {
          if (seq === this.folderRequestSeq && !cached) this.listOfDocuments = []
        })
        .finally(() => {
          if (seq === this.folderRequestSeq) {
            this.loading = false
            this.fieldsLoading = false
          }
        })
    },
    normalizeDocuments (documents) {
      return Array.isArray(documents) ? documents.filter(doc => doc && doc.id) : []
    },
    /* ── search ─────────────────────────────────────────────────────────── */
    onSearchInput () {
      if (!this.isSearching) {
        this.clearSearch()
        return
      }
      this.debouncedSearchPublishedFiles()
    },
    clearSearch () {
      this.searchRequestSeq++ // drops any in-flight result
      this.listOfSearchResult = []
      this.searchLoading = false
    },
    searchPublishedDocuments () {
      const term = (this.fileSearchTerm || '').trim()
      if (!term) {
        this.clearSearch()
        return
      }
      const seq = ++this.searchRequestSeq
      this.searchLoading = true
      hostAppApi.post(`${this.getHostRefApi}search_published_documents`, { user_id: this.userId, search: term })
        .then((res) => {
          if (seq !== this.searchRequestSeq) return
          this.listOfSearchResult = this.normalizeDocuments(res && res.data)
        })
        .catch(() => {
          if (seq === this.searchRequestSeq) this.listOfSearchResult = []
        })
        .finally(() => {
          if (seq === this.searchRequestSeq) this.searchLoading = false
        })
    },
    /* ── viewer ─────────────────────────────────────────────────────────── */
    loadHtmlFile (item) {
      if (!item) return
      if (!item.downloadlink) {
        this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: 'message.common.filenotfound' })
        return
      }
      // prev/next walk the files of the listing the document was opened from, in display order
      if (!this.activeHtmlLink || !this.activeDocuments.some(x => x.id === item.id)) {
        this.activeDocuments = this.visibleFiles.slice()
      }
      this.viewerIndex = this.activeDocuments.findIndex(x => x.id === item.id)
      this.activeDocument = item
      this.activeHtmlTitle = this.nameOf(item)
      this.showPdfPreviewDialog = item.ext === 'pdf'
      this.iframeSrcLoading = item.ext !== 'pdf'
      // start each document at actual size rather than inheriting the last one's zoom
      this.fitToScreen = false
      this.fitScale = 1
      // The listing only carries documents whose rendition exists on the server, so
      // the viewer loads the link directly instead of downloading the file once to
      // probe it and a second time to display it.
      this.activeHtmlLink = item.downloadlink
      this.$nextTick(this.recalculateFitScale)
    },
    closeViewer () {
      this.activeHtmlLink = ''
      this.activeHtmlTitle = ''
      this.activeDocument = null
      this.viewerIndex = -1
    },
    onDocumentError () {
      this.$root.$emit('snackbar', { snackbar: true, color: 'error', text: 'message.common.filenotfound' })
    },
    toggleFitToScreen () {
      this.fitToScreen = !this.fitToScreen
      if (this.fitToScreen) this.$nextTick(this.recalculateFitScale)
      else this.fitScale = 1 // back to 1:1 actual size
    },
    recalculateFitScale () {
      const available = this.$refs.pdfViewport ? this.$refs.pdfViewport.clientWidth : 0
      if (!available) return
      this.viewportWidth = available
      if (!this.fitToScreen) return
      // never scale up past 1 — fitting should shrink an oversized document, not
      // magnify one that already fits
      this.fitScale = Math.min(1, available / this.fitBaseWidth)
    },
    zoomIn () {
      this.applyZoom(this.zoomStep)
    },
    zoomOut () {
      this.applyZoom(-this.zoomStep)
    },
    /* Stepping the scale manually means we're no longer "fitting", so drop that flag
       and stop recalculating on resize — otherwise a rotation would wipe the zoom. */
    applyZoom (delta) {
      const from = this.isScaled ? this.fitScale : 1
      const next = Math.min(this.maxZoom, Math.max(this.minZoom, from + delta))
      this.fitToScreen = false
      this.fitScale = Number(next.toFixed(2))
    }
  },
  watch: {
    '$i18n.locale' () {
      // folder names are translated server-side, so cached listings are stale now
      this.folderCache = {}
      this.navigateToItem(this.activeFolder)
    }
  }
}
</script>
<style>
/* ── browser ─────────────────────────────────────────────────────────────── */
.files-page {
  --files-border: rgba(15, 23, 42, 0.09);
  --files-surface: #ffffff;
  --files-surface-hover: #f7f9fc;
  --files-text: rgba(15, 23, 42, 0.87);
  --files-muted: rgba(15, 23, 42, 0.55);
  --files-accent: var(--v-primary-base, #0377e8);
}
.theme--dark .files-page {
  --files-border: rgba(255, 255, 255, 0.1);
  --files-surface: #16202e;
  --files-surface-hover: #1c2837;
  --files-text: rgba(255, 255, 255, 0.9);
  --files-muted: rgba(255, 255, 255, 0.6);
}
.files-browser {
  padding: 8px 10px 12px;
}
.files-toolbar {
  display: flex;
  align-items: center;
  gap: 8px;
}
.files-search {
  flex: 1 1 auto;
  min-width: 0;
}
.files-search .v-input__slot {
  min-height: 40px !important;
  border-radius: 10px;
}
.files-view-toggle {
  flex: 0 0 auto;
  border-radius: 10px !important;
  border: 1px solid var(--files-border) !important;
  background: var(--files-surface) !important;
}
.files-view-toggle .v-btn {
  height: 38px !important;
  min-width: 40px !important;
  border: 0 !important;
  opacity: 0.7;
}
.files-view-toggle .v-btn.v-btn--active {
  opacity: 1;
  color: var(--files-accent) !important;
}
.files-skeleton-search {
  flex: 1 1 auto;
}
.files-skeleton-tile,
.files-skeleton-tile .v-skeleton-loader__image {
  height: 118px;
  border-radius: 12px;
}
.files-grid--skeleton {
  margin-top: 14px;
}

/* location row */
.files-crumbs {
  display: flex;
  align-items: center;
  gap: 2px;
  margin-top: 8px;
  min-height: 32px;
}
.files-crumbs--search {
  padding-left: 4px;
  color: var(--files-muted);
}
.files-up {
  flex: 0 0 auto;
}
.files-crumb-scroll {
  flex: 1 1 auto;
  min-width: 0;
  display: flex;
  align-items: center;
  white-space: nowrap;
  overflow-x: auto;
  scrollbar-width: none;
  -webkit-overflow-scrolling: touch;
}
.files-crumb-scroll::-webkit-scrollbar {
  display: none;
}
.files-crumb {
  display: inline-flex;
  align-items: center;
  padding: 3px 6px;
  border-radius: 6px;
  font-size: 13px;
  line-height: 1.2;
  color: var(--files-accent);
  background: transparent;
  border: 0;
  cursor: pointer;
  font-family: inherit;
}
.files-crumb:not(:disabled):hover {
  background: var(--files-surface-hover);
}
.files-crumb:focus-visible {
  outline: 2px solid var(--files-accent);
  outline-offset: 1px;
}
.files-crumb--current {
  color: var(--files-text);
  font-weight: 600;
  cursor: default;
}
.files-crumb-home {
  color: inherit !important;
}
.files-crumb-sep {
  color: var(--files-muted) !important;
  flex: 0 0 auto;
}
.files-progress {
  margin-top: 2px;
}
.files-count {
  display: inline-block;
  min-width: 20px;
  padding: 0 6px;
  margin-left: 4px;
  border-radius: 10px;
  font-size: 11px;
  line-height: 18px;
  text-align: center;
  font-weight: 600;
  color: var(--files-muted);
  background: var(--files-surface-hover);
  border: 1px solid var(--files-border);
}

/* content */
.files-content {
  transition: opacity 0.15s ease;
}
.files-content--stale {
  opacity: 0.55;
  pointer-events: none;
}
.files-section + .files-section {
  margin-top: 6px;
}
.files-section-title {
  display: flex;
  align-items: center;
  margin: 10px 4px 6px;
  font-size: 11.5px;
  font-weight: 600;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: var(--files-muted);
}
.files-grid {
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(150px, 1fr));
  gap: 10px;
}
@media (max-width: 600px) {
  .files-grid {
    grid-template-columns: repeat(auto-fill, minmax(108px, 1fr));
    gap: 8px;
  }
}
.files-tile {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: flex-start;
  gap: 6px;
  min-height: 118px;
  padding: 12px 8px 10px;
  border-radius: 12px;
  border: 1px solid var(--files-border);
  background: var(--files-surface);
  color: var(--files-text);
  text-align: center;
  cursor: pointer;
  font-family: inherit;
  -webkit-tap-highlight-color: transparent;
  transition: box-shadow 0.15s ease, border-color 0.15s ease, transform 0.15s ease, background-color 0.15s ease;
}
.files-tile:hover {
  background: var(--files-surface-hover);
  border-color: rgba(3, 119, 232, 0.35);
  box-shadow: 0 4px 14px rgba(15, 23, 42, 0.08);
  transform: translateY(-1px);
}
.files-tile:active {
  transform: scale(0.98);
}
.files-tile:focus-visible {
  outline: 2px solid var(--files-accent);
  outline-offset: 2px;
}
.files-tile-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 52px;
  height: 52px;
  border-radius: 14px;
  flex: 0 0 auto;
}
.files-tile-name {
  display: -webkit-box;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
  width: 100%;
  font-size: 13px;
  font-weight: 500;
  line-height: 1.3;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.files-tile-meta {
  font-size: 11px;
  line-height: 1.2;
  color: var(--files-muted);
  letter-spacing: 0.02em;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* list mode */
.files-list {
  background: transparent !important;
  margin-top: 8px;
  border: 1px solid var(--files-border);
  border-radius: 12px;
  overflow: hidden;
}
.files-row {
  min-height: 54px !important;
  background: var(--files-surface);
}
.files-row + .files-row {
  border-top: 1px solid var(--files-border);
}
.files-row-icon {
  border-radius: 10px !important;
}
.files-row-name {
  font-size: 14px !important;
  font-weight: 500;
  color: var(--files-text) !important;
}
.files-row-meta {
  font-size: 12px !important;
  color: var(--files-muted) !important;
}
.files-row-chevron {
  color: var(--files-muted) !important;
}

/* empty state */
.files-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  padding: 48px 16px 40px;
  text-align: center;
}
.files-empty-icon {
  color: var(--files-muted) !important;
  opacity: 0.6;
}
.files-empty-title {
  margin: 14px 0 4px;
  font-size: 15px;
  font-weight: 500;
  color: var(--files-text);
}
.files-empty-hint {
  margin: 0;
  font-size: 13px;
  color: var(--files-muted);
}

/* ── viewer ──────────────────────────────────────────────────────────────── */
.files-viewer-head {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 6px 8px;
  border-bottom: 1px solid var(--files-border);
}
.files-viewer-icon {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 30px;
  height: 30px;
  border-radius: 8px;
  flex: 0 0 auto;
}
.files-viewer-title {
  flex: 1 1 auto;
  min-width: 0;
  font-size: 14px;
  font-weight: 500;
  color: var(--files-text);
}
.files-viewer-nav {
  display: inline-flex;
  align-items: center;
  gap: 2px;
  flex: 0 0 auto;
}
.files-viewer-counter {
  font-size: 12px;
  color: var(--files-muted);
  min-width: 44px;
  text-align: center;
  font-variant-numeric: tabular-nums;
}
.files-html {
  height: 73vh !important;
}
.button__box{
  position: fixed;
  bottom: 60px;
  right: 10px;
  background-color: #424242;
  z-index: 9999;
  display: flex;
  flex-direction: column;
  border-radius: 0 !important;
}
.button__box button{
  padding: 7px 14px;
  color: #fff;
  font-size: 24px;
  line-height: 1;
}
.button__box button:hover:not(:disabled){
  background-color: #707070;
}
.button__box button:disabled{
  opacity: 0.4;
  cursor: default;
}
.main_pdf_view{
  overflow-x: auto;
  background-color: #fff !important;
}
/* fitting means everything is inside the viewport, so no sideways scrolling */
.main_pdf_view--fit{
  overflow-x: hidden;
}
/* fit sits between + and -, divided off so the group reads as zoom / fit / zoom */
.button__box-fit{
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 9px 14px !important;
  border-top: 1px solid #707070;
  border-bottom: 1px solid #707070;
}
</style>
